import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import { InputHTMLAttributes, useEffect, useId, useState } from 'react';
import type { DayPickerBase } from 'react-day-picker';
import { offset, shift } from '@floating-ui/react';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import { isFirefox, isiOS, isSafari } from '@tmh-website/services/utils.service';
import { DropdownMenuContext } from '@tmh-website/shared';
import Calendar from '@tmh-website/ui/Calendar';
import DropdownMenu from '@tmh-website/ui/DropdownMenu';
import { type FormElementCommonProps, StyledFormElements, formElementThemes } from '@tmh-website/ui/FormElements';

import * as Styled from './DatePicker.styles';
import { datePickerThemes } from './DatePicker.theme';

export interface DatePickerProps
  extends Omit<FormElementCommonProps, 'placeholder'>,
    Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'value'>> {
  disabledDays?: DayPickerBase['disabled'];
  helpText?: string;
  value?: Date;
  year?: { from: number; to: number };
}

const DatePicker = ({
  disabled,
  disabledDays,
  helpText,
  label,
  name,
  placeholder,
  required,
  themeColor,
  year,
  ...inputProps
}: DatePickerProps) => {
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { errors, touched, values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const hasError = get(errors, name) && get(touched, name);
  const value = get(values, name);
  const [inputValue, setInputValue] = useState('');
  const [dropdownMenuForceCloseCounter, setDropdownMenuForceCloseCounter] = useState(0);

  // TODO fix when this issue (https://github.com/jaredpalmer/formik/issues/2059) is properly resolved by
  // merging this pr: https://github.com/jaredpalmer/formik/pull/2384
  // `setFieldValue` actually returns a promise we need to await, but the types are wrong in the Formik library.
  const handleOnChange = async (date?: Date) => {
    if (isValid(date)) {
      await setFieldValue(name, date);
    } else {
      await setFieldValue(name, '');
    }

    setFieldTouched(name, true);
  };

  const currentYear = new Date().getFullYear();

  const setValueToCurrentDate = () => {
    const defaultValue = new Date();

    setInputValue(format(defaultValue, 'yyyy-MM-dd'));
    setFieldValue(name, defaultValue);
  };

  useEffect(() => {
    if (value && isValid(value)) {
      setInputValue(format(value, 'yyyy-MM-dd'));
    } else {
      setInputValue('');
    }
  }, [value]);

  // Safari exhibits unusual behavior with date inputs.
  // When the value is empty, it displays the current date as a placeholder.
  // To avoid confusing the User, we set the input value to the current date
  // (to keep the value and placeholder in sync).
  useEffect(() => {
    if (isSafari() && (value === undefined || value === null || value === '')) {
      setValueToCurrentDate();
    }
  }, []);

  return (
    <div>
      <DropdownMenuContext.Provider
        value={{
          _forceCloseCounter: dropdownMenuForceCloseCounter,
          forceClose: () => setDropdownMenuForceCloseCounter(dropdownMenuForceCloseCounter + 1),
        }}>
        <DropdownMenu
          themeColor={formElementThemes[themeColor].dropdownThemeColor}
          disabled={disabled}
          closeOnClick={false}
          noPadding
          customMiddleware={[offset({ mainAxis: 4 }), shift()]}
          anchorEl={
            <Styled.Container
              $themeColor={themeColor}
              $hasError={hasError}
              $isDisabled={disabled}
              $disableSmallLabelOnFocus>
              {label && (
                <StyledFormElements.InputLabel $themeColor={themeColor} $fieldIsFilled htmlFor={nameWithId}>
                  {label}
                  {required && '*'}
                </StyledFormElements.InputLabel>
              )}
              <Styled.Input
                {...inputProps}
                $themeColor={themeColor}
                $smallLabel
                onFocus={e => {
                  e.preventDefault();
                }}
                onClick={e => {
                  e.preventDefault();
                }}
                onChange={e => setInputValue(e.currentTarget.value)}
                onBlur={e => {
                  const parsed = parse(e.currentTarget.value, 'yyyy-MM-dd', new Date());

                  if (isValid(parsed)) {
                    handleOnChange(parsed);
                  }
                }}
                id={nameWithId}
                placeholder={placeholder}
                name={name}
                value={inputValue}
                disabled={disabled}
                type='date'
                style={{ pointerEvents: isiOS() ? 'none' : undefined }}
              />
              {/* TODO: This is here because there currently is no way to hide the icon in Firefox. Remove when fixed. */}
              {/* @see https://bugzilla.mozilla.org/show_bug.cgi?id=1812397 */}
              {isFirefox() && <Styled.FirefoxIconHider $themeColor={themeColor} />}
              <StyledFormElements.ExtraElement>
                <Styled.Icon size={20} />
              </StyledFormElements.ExtraElement>
            </Styled.Container>
          }>
          <Calendar
            mode='single'
            onSelect={date => handleOnChange(date)}
            themeColor={datePickerThemes[themeColor].calendarThemeColor}
            required={required}
            selected={value ? value : undefined}
            fromYear={year ? year.from : currentYear - 30}
            toYear={year ? year.to : currentYear + 30}
            disabled={disabledDays}
            defaultMonth={value ? value : undefined}
          />
        </DropdownMenu>
      </DropdownMenuContext.Provider>

      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default DatePicker;
